import { useAppDispatch, useAppSelector, useOverridableFeatureFlags, withAsyncThunkErrorHandling } from "@lib/store";
import { IconButton } from "@molecules";

import { cn } from "@utils";
import React, { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { ErrorObject } from "../types";
import { WorkdayStatus } from "@lib/store/src/data/timeslots/apiTypes";
import { actions } from "@lib/store/src/data/timeslots/actions";
import { IconStatusMapping } from "./utils";
import { useEditWorkday } from "./QuickOfficeDayButton";

type Props = {
  disabled: boolean;
  date: string;

  processingFor: WorkdayStatus | null;
  setProcessingFor: Dispatch<SetStateAction<WorkdayStatus | null>>;

  loading: boolean;
  setLoading: Dispatch<SetStateAction<boolean>>;

  errorObject: ErrorObject;
  setErrorObject: Dispatch<SetStateAction<ErrorObject>>;
};

export const QuickWorkingRemotelyButton = ({
  disabled,
  date: dateKey,
  loading,
  setLoading,
  errorObject,
  setErrorObject,
  processingFor,
  setProcessingFor,
}: Props) => {
  const features = useOverridableFeatureFlags();
  const forWorkdayStatus = "WorkingRemotely" satisfies WorkdayStatus;

  const { t } = useTranslation();
  const openEditWorkdayModal = useEditWorkday().openModal;
  const dispatch = useAppDispatch();

  // TODO: support multiple days in this UI
  const workday = useAppSelector(state => {
    const entry = state.data.timeslots.workdaysByDate[dateKey];
    return entry?.data?.at(0) ?? null;
  });

  const createWorkingRemotelyWorkday = async () => {
    setLoading(true);
    setErrorObject(p => ({
      ...p,
      workday: null,
    }));
    setProcessingFor(forWorkdayStatus);

    const { success } = await dispatch(
      withAsyncThunkErrorHandling(() =>
        actions.createWorkday({
          status: forWorkdayStatus,
          // TODO: support non full day workdays
          localStart: `${dateKey}T00:00`,
          localEnd: `${dateKey}T23:59`,
        }),
      ),
    );

    if (!success) {
      setErrorObject(p => ({
        ...p,
        workday: t("workplace:CouldntFindASpot"),
      }));
    }

    setProcessingFor(null);
    setLoading(false);
  };

  const handleClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    e.stopPropagation();

    if (workday) {
      openEditWorkdayModal(workday, dateKey);
    } else {
      createWorkingRemotelyWorkday();
    }
  };

  return (
    <>
      <IconButton
        className={cn("shrink-0", {
          "bg-energizing-yellow-500*": processingFor === forWorkdayStatus || workday?.status === forWorkdayStatus,
        })}
        disabled={disabled}
        iconButton="tertiary"
        icon={IconStatusMapping[forWorkdayStatus]}
        onClick={handleClick}
      />
      {!loading && workday && (
        <p className="truncate leading-none text-mapiq-black-500">{t("workplace:WorkingRemotely")}</p>
      )}
    </>
  );
};
